import type { SiteReviewContext } from '@epc/contracts';
import { type OfflineScope, reportKey } from '../offline/store';
import { initialReviewInput, pendingDecision, type ReviewCommand, type ReviewInput } from './model';

export interface ReviewRecord {
  key: string;
  localRevision: number;
  context: SiteReviewContext;
  input: ReviewInput;
  command: ReviewCommand | null;
}
/** Отдельное хранилище проверки не меняет схему авторской очереди. */
export class ReviewStore {
  private constructor(private db: IDBDatabase) {}
  static open(factory: IDBFactory = indexedDB, name = 'epc-site-review'): Promise<ReviewStore> {
    return new Promise((resolve, reject) => {
      const r = factory.open(name, 1);
      let blocked = false;
      r.onupgradeneeded = () => r.result.createObjectStore('decisions', { keyPath: 'key' });
      r.onblocked = () => {
        blocked = true;
        reject(new Error('Закройте другую вкладку проверки'));
      };
      r.onerror = () => reject(r.error);
      r.onsuccess = () => {
        if (blocked) {
          r.result.close();
          return;
        }
        r.result.onversionchange = () => r.result.close();
        resolve(new ReviewStore(r.result));
      };
    });
  }
  close() {
    this.db.close();
  }
  private run<T>(
    scope: OfflineScope,
    id: string,
    write: boolean,
    fn: (row: ReviewRecord | null) => { row: ReviewRecord | null; result: T },
  ): Promise<T> {
    return new Promise((resolve, reject) => {
      const tx = this.db.transaction('decisions', write ? 'readwrite' : 'readonly');
      const store = tx.objectStore('decisions');
      const key = reportKey(scope, id);
      const req = store.get(key);
      let result: T;
      let failure: unknown;
      req.onsuccess = () => {
        try {
          const row = (req.result ?? null) as ReviewRecord | null;
          if (
            row &&
            (row.key !== key ||
              row.context.report.id !== id ||
              row.context.report.tenantId !== scope.tenantId)
          )
            throw new Error('Неверное пространство проверки');
          const next = fn(row);
          result = next.result;
          if (write && next.row) {
            if (next.row.key !== key) throw new Error('Нельзя менять владельца решения');
            store.put(next.row);
          }
        } catch (e) {
          failure = e;
          tx.abort();
        }
      };
      tx.oncomplete = () => resolve(result);
      tx.onabort = () => reject(failure ?? tx.error ?? new Error('Проверка не сохранена'));
      tx.onerror = () => {};
    });
  }
  read(scope: OfflineScope, id: string) {
    return this.run(scope, id, false, (row) => ({ row, result: row }));
  }
  update(scope: OfflineScope, id: string, revision: number, fn: (row: ReviewRecord | null) => ReviewRecord) {
    return this.run(scope, id, true, (row) => {
      if ((row?.localRevision ?? 0) !== revision)
        throw new Error('Проверка изменена в другой вкладке. Ваш ввод оставлен на экране');
      const next = fn(row);
      next.localRevision = revision + 1;
      return { row: next, result: next };
    });
  }
  start(scope: OfflineScope, context: SiteReviewContext, revision: number) {
    return this.update(scope, context.report.id, revision, (row) => {
      if (pendingDecision(row?.command ?? null))
        throw new Error('Сначала уточните исход отправленного решения');
      return {
        key: reportKey(scope, context.report.id),
        localRevision: 0,
        context,
        input: initialReviewInput(context.report),
        command: null,
      };
    });
  }
  save(scope: OfflineScope, id: string, revision: number, input: ReviewInput) {
    return this.update(scope, id, revision, (row) => {
      if (!row || row.command) throw new Error('Решение уже отправлено. Начните проверку актуальной версии');
      return { ...row, input };
    });
  }
  begin(scope: OfflineScope, id: string, revision: number, command: ReviewCommand) {
    return this.update(scope, id, revision, (row) => {
      if (!row || row.command) throw new Error('Решение уже отправлено');
      return { ...row, command };
    });
  }
  finish(scope: OfflineScope, id: string, command: ReviewCommand) {
    // Ответ другой вкладки не может переписать более новое решение или успешный ACK.
    return this.run(scope, id, true, (row) => {
      if (!row || row.command?.key !== command.key || row.command.requestJson !== command.requestJson)
        throw new Error('Решение изменилось');
      if (row.command.state === 'done') return { row, result: row };
      const next = { ...row, localRevision: row.localRevision + 1, command };
      return { row: next, result: next };
    });
  }
}
